import React from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import Platform from "react-native-web/dist/exports/Platform";
import ImageBackground from "react-native-web/dist/exports/ImageBackground";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import { applyShadowToken } from '../ThemeProvider';
import { getTokensPropType, responsiveProps, useResponsiveProp, formatImageSource } from '../utils';
import { a11yProps, viewProps, selectSystemProps } from '../utils/props';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);

// Ensure explicit selection of tokens
const selectStyles = _ref => {
  let {
    flex,
    backgroundColor,
    borderColor,
    borderRadius,
    borderWidth,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    minWidth,
    shadow,
    backgroundGradient,
    gradient,
    maxHeight,
    overflowY
  } = _ref;
  return {
    flex,
    backgroundColor,
    borderColor,
    borderRadius,
    borderWidth,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    minWidth,
    ...applyShadowToken(shadow),
    ...(gradient && Platform.OS === 'web' ? {
      backgroundImage: `linear-gradient(${gradient.angle}deg, ${gradient.stops[0].color}, ${gradient.stops[1].color})`,
      backgroundOrigin: `border-box`,
      boxShadow: `inset 0 1000px white`,
      border: `${borderWidth}px solid transparent`
    } : {}),
    ...(backgroundGradient && Platform.OS === 'web' ? {
      backgroundImage: `linear-gradient(${backgroundGradient.angle}deg, ${backgroundGradient.stops[0].color}, ${backgroundGradient.stops[1].color})`
    } : {}),
    ...(Platform.OS === 'web' ? {
      maxHeight,
      overflowY
    } : {})
  };
};

/**
 * A themeless base component for Card which components can apply theme tokens to. Not
 * intended to be used in apps or sites directly: build themed components on top of this.
 */
const CardBase = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    children,
    tokens,
    dataSet,
    backgroundImage,
    ...rest
  } = _ref2;
  const cardStyle = selectStyles(typeof tokens === 'function' ? tokens() : tokens);
  const props = selectProps(rest);
  const {
    src = '',
    alt = '',
    resizeMode = ''
  } = backgroundImage || {};
  const backgroundImageResizeMode = useResponsiveProp(resizeMode, 'cover');
  const imageSourceViewport = formatImageSource(useResponsiveProp(src));
  return /*#__PURE__*/_jsx(View, {
    style: cardStyle,
    dataSet: dataSet,
    ref: ref,
    ...props,
    children: src ? /*#__PURE__*/_jsx(ImageBackground, {
      source: imageSourceViewport,
      imageStyle: {
        borderRadius: cardStyle?.borderRadius - cardStyle?.borderWidth
      },
      resizeMode: backgroundImageResizeMode,
      style: styles.imageBackground,
      accessible: true,
      accessibilityLabel: alt,
      children: children
    }) : children
  });
});
CardBase.displayName = 'CardBase';
const styles = StyleSheet.create({
  imageBackground: {
    width: '100%',
    height: '100%'
  }
});
CardBase.propTypes = {
  ...selectedSystemPropTypes,
  children: PropTypes.node,
  tokens: getTokensPropType('Card'),
  /**
   * Apply background image to the card.
   */
  backgroundImage: PropTypes.shape({
    // The image src is either a URI string or a number (when a local image src is bundled in IOS or Android app)
    // src is an object when used responsively to provide different image sources for different screen sizes
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]).isRequired,
    alt: PropTypes.string,
    resizeMode: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(['cover', 'contain', 'stretch', 'repeat', 'center']))
  })
};
export default CardBase;